import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Flame, AlertTriangle, Calendar, Phone, DollarSign, ChevronRight } from "lucide-react";
import { API, useAuth } from "../../App";

/**
 * Today's Actions tray (Phase B / P4).
 *
 * 5 tappable rows. Each opens a filtered destination so Bryan can act in
 * one tap from his phone. Sits above all existing Dashboard cards.
 */

const ROW_CONFIG = [
  {
    key: "hot",
    icon: Flame,
    color: "bg-red-500",
    textColor: "text-red-600",
    label: "Hot leads — call these first",
    countKey: "hot_count",
    href: "/admin/priority-queue",
  },
  {
    key: "at_risk",
    icon: AlertTriangle,
    color: "bg-amber-500",
    textColor: "text-amber-600",
    label: "At-risk deals",
    countKey: "at_risk_count",
    href: "/admin/leads?at_risk=1",
  },
  {
    key: "tour_pending",
    icon: Calendar,
    color: "bg-blue-500",
    textColor: "text-blue-600",
    label: "Tour invitations pending",
    countKey: "tour_pending_count",
    href: "/admin/leads?tour_pending=1",
  },
  {
    key: "needs_followup",
    icon: Phone,
    color: "bg-emerald-500",
    textColor: "text-emerald-600",
    label: "Leads need follow-up today",
    countKey: "needs_followup_count",
    href: "/admin/priority-queue",
  },
  {
    key: "pipeline_revenue",
    icon: DollarSign,
    color: "bg-emerald-600",
    textColor: "text-emerald-700",
    label: "Estimated pipeline revenue",
    href: "/admin/leases",
    isRevenue: true,
  },
];

const money = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "$0";

const TodaysActions = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/todays-actions`, { headers });
        if (!cancelled) setData(r.data);
      } catch (e) {
        console.error("todays-actions fetch failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="todays-actions-loading">
        <div className="animate-pulse space-y-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={`skeleton-${i}`} className="h-14 bg-slate-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const funnel = data?.funnel || {};
  const rates = data?.funnel_rates || {};

  return (
    <div className="space-y-4" data-testid="todays-actions-tray">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 bg-[#1F2A44] text-white flex items-center justify-between">
          <h2 className="font-bold text-sm uppercase tracking-wider">Today's Actions</h2>
          <span className="text-[11px] text-white/60">Live</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {ROW_CONFIG.map((row, idx) => {
            const Icon = row.icon;
            const value = row.isRevenue
              ? money(data?.pipeline_revenue)
              : (data?.[row.countKey] ?? 0);
            return (
              <motion.li
                key={row.key}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
              >
                <button
                  type="button"
                  onClick={() => navigate(row.href)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 active:bg-slate-100 transition text-left"
                  data-testid={`todays-action-${row.key}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${row.color} text-white flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-2xl font-bold ${row.textColor}`}>{value}</p>
                    <p className="text-sm text-slate-600 truncate">{row.label}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                </button>
              </motion.li>
            );
          })}
        </ul>
      </div>

      {/* Funnel conversion bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="funnel-conversion-bar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm text-[#1F2A44]">Funnel</h3>
          <span className="text-[11px] text-slate-400 uppercase tracking-wider">Lead lifecycle</span>
        </div>
        <div className="grid grid-cols-4 gap-2 items-stretch">
          {[
            { label: "New",       count: funnel.new ?? 0,       color: "bg-slate-400" },
            { label: "Contacted", count: funnel.contacted ?? 0, color: "bg-blue-500", rate: rates.new_to_contacted },
            { label: "Touring",   count: funnel.touring ?? 0,   color: "bg-amber-500", rate: rates.contacted_to_touring },
            { label: "Converted", count: funnel.converted ?? 0, color: "bg-emerald-500", rate: rates.touring_to_converted },
          ].map((stage) => (
            <div key={stage.label} className="text-center" data-testid={`funnel-stage-${stage.label.toLowerCase()}`}>
              <div className={`h-2 rounded-full ${stage.color} mb-2`} />
              <p className="text-2xl font-bold text-[#1F2A44]">{stage.count}</p>
              <p className="text-xs text-slate-500">{stage.label}</p>
              {stage.rate !== undefined && (
                <p className="text-[10px] text-slate-400 mt-1">{stage.rate}% conv</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TodaysActions;
